/**
 * `useErrorHandler` — the single routing point for query/mutation errors (frontend.md §4.2).
 *
 * frontend.md §4.2 mandates three handling paths, one per PRD §13.3 category:
 *  - **`user`** — rendered INLINE on the surface with actionable guidance. The hook does
 *    NOT toast these; it returns them so the surface can place them. (A surface that wants
 *    the default treatment can pass `inlineUser: false` to also toast.)
 *  - **`system`** — these are normally caught by a `<ErrorBoundary>`; if one reaches a
 *    query `onError` (a non-render-phase failure), the hook toasts it with a "copy error
 *    report" affordance and logs the full envelope context locally.
 *  - **`external`** — a non-blocking toast with a retry affordance when `retryable` is
 *    true. The frontend does NOT retry itself (the backend owns backoff) — the toast's
 *    retry button re-issues the query on explicit user action.
 *
 * "No silent failures" (frontend.md §4.2 / conventions.md §3): every `@tanstack/query`
 * `onError` must route here. A hook that fetches data wires `onError: handleError`.
 */

import { useCallback } from 'react';
import { AletheiaApiError, type ErrorEnvelope } from '@/types';
import { getLogger } from '@/api';
import { useToastStore } from './useToastStore';

const log = getLogger('hooks.error');

/** Normalize any thrown value into an `ErrorEnvelope` — the hook accepts `unknown` from RQ. */
export function toErrorEnvelope(err: unknown): ErrorEnvelope {
  if (err instanceof AletheiaApiError) return err.envelope;
  // A non-AletheiaApiError reaching here is a frontend bug (a thrown string, a render
  // error that escaped a boundary) — categorize as `system`, the loudest path.
  return {
    category: 'system',
    code: 'internal_error',
    message: err instanceof Error ? err.message : 'An unexpected error occurred.',
    retryable: false,
    context: undefined,
    queryId: undefined,
  };
}

interface UseErrorHandlerOptions {
  /**
   * When true (the default), `user`-category errors are NOT toasted — the surface renders
   * them inline. Set false to also toast user errors (rare; most surfaces inline them).
   */
  inlineUser?: boolean;
}

export interface ErrorHandlerResult {
  /** Pass this as a query/mutation `onError`. */
  handleError: (err: unknown, retry?: () => void) => ErrorEnvelope;
}

export function useErrorHandler(options: UseErrorHandlerOptions = {}): ErrorHandlerResult {
  const { inlineUser = true } = options;
  const pushToast = useToastStore((s) => s.push);

  const handleError = useCallback(
    (err: unknown, retry?: () => void): ErrorEnvelope => {
      const envelope = toErrorEnvelope(err);

      // Always log locally — structured, no secrets (the logger redacts). queryId joins
      // the frontend line to the backend's log for the same request.
      log.error(
        'query_error',
        { category: envelope.category, code: envelope.code, context: envelope.context },
        envelope.queryId,
      );

      // Web/connection loss is reflected ONLY in the bottom-left status indicator ("Web
      // Services Unreachable"), never as a toast — otherwise every query in flight at the
      // instant a connection drops stacks its own "Connection issue" toast (the UI
      // explosion). The connectivity manager — driven by the same transport signal — owns
      // the feedback, and the persisted cache keeps the app usable. We still logged it above.
      if (envelope.code === 'dependency_unavailable') {
        return envelope;
      }

      switch (envelope.category) {
        case 'user':
          // UX gap — surfaced inline by the surface (frontend.md §4.2). Toast only if asked.
          if (!inlineUser) {
            pushToast({ kind: 'error', title: 'Action needed', message: envelope.message });
          }
          break;
        case 'system':
          // Bug/invariant — a recoverable toast with a copy-report affordance.
          pushToast({
            kind: 'error',
            title: 'Something went wrong',
            message: envelope.message,
            errorReport: envelope,
          });
          break;
        case 'external':
          // API/network — non-blocking toast; retry affordance only when retryable.
          pushToast({
            kind: 'warning',
            title: 'Connection issue',
            message: envelope.message,
            retry: envelope.retryable ? retry : undefined,
          });
          break;
      }
      return envelope;
    },
    [inlineUser, pushToast],
  );

  return { handleError };
}
